import { SagaIterator } from 'redux-saga';
import { call, cancel, put, select, takeEvery } from 'redux-saga/effects';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { PS20Logger } from '@ps20-frontend/logging';
import { App, isAppTypeInList } from '@ps20-frontend/frontend-models';
import {
  getEndpointUrl,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  HTTP_METHOD,
  WrappedSavedStates,
  WrappedSavedStatesLoaded,
} from '@ps20-frontend/shop-integration-redux';
import { getAllUwContexts } from '@ps20-frontend/uw';
import { IAppState } from '@ps20-frontend/apps/dashboard-store';
import { DashboardAction, DashboardActions } from '../dashboard.actions';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { shouldUseSdwEndpoint } from '../selectors/transient-state.selector';

export const SDS_EXITING_FILES_URL = '/privatschutz/api/sds/files';

export enum FILES_RETRIEVE_EVENTS {
  FILES_RETRIEVE_SUCCESSFUL = 'FILES_RETRIEVE_SUCCESSFUL_EVENT',
  FILES_RETRIEVE_FAILED = 'FILES_RETRIEVE_FAILED',
}

export function* watchFilesAfterLoad(): SagaIterator {
  yield takeEvery(GlobalActions.SAVED_STATES_LOADED, safeSaga(checkFiles, App.SHOP_DASHBOARD));
  yield takeEvery(
    FILES_RETRIEVE_EVENTS.FILES_RETRIEVE_SUCCESSFUL,
    safeSaga(successCallback, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    FILES_RETRIEVE_EVENTS.FILES_RETRIEVE_FAILED,
    safeSaga(errorCallback, App.SHOP_DASHBOARD),
  );
}

export function* checkFiles(action: GlobalAction<WrappedSavedStatesLoaded>): SagaIterator {
  const actionPayload: WrappedSavedStates = action.payload.savedStatesLoaded;
  if (!actionPayload.some(loadedState => loadedState.appId === App.SHOP_DASHBOARD)) {
    return yield cancel();
  }
  const currentState: IAppState = yield select();
  const dashboardProdukte: WarenkorbAngebot[] = (currentState.warenkorb ?? []).filter(produkt =>
    isAppTypeInList(produkt.appId, [App.VWG, App.VHV]),
  );
  if (!dashboardProdukte || dashboardProdukte.length === 0) {
    return yield cancel();
  }
  let filesToDownload = [] as string[];

  const registeredContextKeys = new Set(getAllUwContexts().map(descriptor => descriptor.key));

  dashboardProdukte.forEach(dashboardProdukt => {
    (dashboardProdukt.uw?.entries ?? [])
      .filter(entry => registeredContextKeys.has(entry.contextKey))
      .forEach(entry => {
        filesToDownload = filesToDownload.concat(entry.files.map(file => file.uploadId));
      });
  });

  if (filesToDownload.length === 0) {
    return yield cancel();
  }

  yield call(callBackend, filesToDownload);
}

export function* callBackend(sdsFileRequest: string[]): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().doRequest({
      method: HTTP_METHOD.GET,
      appId: App.SHOP_DASHBOARD,
      url: getEndpointUrl(shouldUseSdwEndpoint(state), SDS_EXITING_FILES_URL + '/' + sdsFileRequest),
      successEvent: FILES_RETRIEVE_EVENTS.FILES_RETRIEVE_SUCCESSFUL,
      errorEvent: FILES_RETRIEVE_EVENTS.FILES_RETRIEVE_FAILED,
    }),
  );
}

export function* successCallback(successAction: DashboardAction<Array<string>>): SagaIterator {
  const resp: Array<string> = successAction.payload;
  yield put(new DashboardActions().detectNotExistingFiles(resp));
}

export function* errorCallback(): SagaIterator {
  yield call(
    [PS20Logger, PS20Logger.error],
    App.SHOP_DASHBOARD,
    'SDS file existence lookup failed; keeping the stored file references untouched',
  );
}
